import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { apiClient } from '../utils/api';

interface Author {
  firstName?: string;
  lastName?: string;
  teamName: string;
}

interface NewsPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  headerImage?: string;
  publishDate: string;
  status: 'draft' | 'published';
  author: Author;
  createdAt: string;
  updatedAt: string;
}

interface NewsResponse {
  posts: NewsPost[];
  totalCount: number;
  hasMore: boolean;
}

const News: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  const [newsData, setNewsData] = useState<NewsResponse | null>(null);
  const [selectedPost, setSelectedPost] = useState<NewsPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Load all news posts or single post
  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Reset state when switching between views
        setSelectedPost(null);
        setNewsData(null);

        if (slug) {
          // Fetch single news post with caching
          const cacheKey = `news_post_${slug}`;
          const cacheTimestampKey = `news_post_timestamp_${slug}`;
          
          const cachedPost = sessionStorage.getItem(cacheKey);
          const cacheTimestamp = sessionStorage.getItem(cacheTimestampKey);
          
          if (cachedPost && cacheTimestamp) {
            try {
              const timestamp = parseInt(cacheTimestamp);
              const now = Date.now();
              const oneHour = 15 * 60 * 1000; // 1 hour in milliseconds
              
              // Check if cache is still valid (less than 1 hour old)
              if (now - timestamp < oneHour) {
                const parsed = JSON.parse(cachedPost);
                setSelectedPost(parsed);
                setLoading(false);
                return;
              } else {
                // Cache expired, remove it
                sessionStorage.removeItem(cacheKey);
                sessionStorage.removeItem(cacheTimestampKey);
              }
            } catch (e) {
              // If parsing fails, remove invalid cache
              sessionStorage.removeItem(cacheKey);
              sessionStorage.removeItem(cacheTimestampKey);
            }
          }
          
          const response = await apiClient.get(`/news/${slug}`);
          setSelectedPost(response.data);
          
          // Cache the post with timestamp
          sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
          sessionStorage.setItem(cacheTimestampKey, Date.now().toString());
        } else {
          // Fetch all news posts with caching
          const cacheKey = 'news_listing_data';
          const cacheTimestampKey = 'news_listing_timestamp';
          
          const cachedNews = sessionStorage.getItem(cacheKey);
          const cacheTimestamp = sessionStorage.getItem(cacheTimestampKey);
          
          if (cachedNews && cacheTimestamp) {
            try {
              const timestamp = parseInt(cacheTimestamp);
              const now = Date.now();
              const oneHour = 15 * 60 * 1000; // 1 hour in milliseconds
              
              // Check if cache is still valid (less than 1 hour old)
              if (now - timestamp < oneHour) {
                const parsed = JSON.parse(cachedNews);
                setNewsData(parsed);
                setLoading(false);
                return;
              } else {
                // Cache expired, remove it
                sessionStorage.removeItem(cacheKey);
                sessionStorage.removeItem(cacheTimestampKey);
              }
            } catch (e) {
              // If parsing fails, remove invalid cache
              sessionStorage.removeItem(cacheKey);
              sessionStorage.removeItem(cacheTimestampKey);
            }
          }
          
          const response = await apiClient.get('/news?limit=10');
          setNewsData(response.data);
          
          // Cache the news listing with timestamp
          sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
          sessionStorage.setItem(cacheTimestampKey, Date.now().toString());
        }
      } catch (error: any) {
        setError(error.response?.data?.message || 'Failed to load news');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [slug]);

  const loadMorePosts = async () => {
    if (!newsData || loadingMore || !newsData.hasMore) return;

    try {
      setLoadingMore(true);
      
      // Check if we have cached data for this offset
      const cacheKey = `news_listing_offset_${newsData.posts.length}`;
      const cacheTimestampKey = `news_listing_offset_timestamp_${newsData.posts.length}`;
      
      const cachedMorePosts = sessionStorage.getItem(cacheKey);
      const cacheTimestamp = sessionStorage.getItem(cacheTimestampKey);
      
      if (cachedMorePosts && cacheTimestamp) {
        try {
          const timestamp = parseInt(cacheTimestamp);
          const now = Date.now();
          const oneHour = 15 * 60 * 1000; // 1 hour in milliseconds
          
          // Check if cache is still valid (less than 1 hour old)
          if (now - timestamp < oneHour) {
            const parsed = JSON.parse(cachedMorePosts);
            setNewsData({
              ...parsed,
              posts: [...newsData.posts, ...parsed.posts]
            });
            setLoadingMore(false);
            return;
          } else {
            // Cache expired, remove it
            sessionStorage.removeItem(cacheKey);
            sessionStorage.removeItem(cacheTimestampKey);
          }
        } catch (e) {
          // If parsing fails, remove invalid cache
          sessionStorage.removeItem(cacheKey);
          sessionStorage.removeItem(cacheTimestampKey);
        }
      }
      
      const response = await apiClient.get(`/news?limit=10&offset=${newsData.posts.length}`);
      const newData = {
        ...response.data,
        posts: [...newsData.posts, ...response.data.posts]
      };
      setNewsData(newData);
      
      // Cache the additional posts with timestamp
      sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
      sessionStorage.setItem(cacheTimestampKey, Date.now().toString());
    } catch (error) {
      console.error('Error loading more posts:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatAuthor = (author: Author): string => {
    if (author.firstName && author.lastName) {
      return `${author.firstName} ${author.lastName}`;
    }
    return author.teamName;
  };

  const stripHtmlTags = (html: string): string => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  const getExcerpt = (content: string, maxLength: number = 150): string => {
    const text = stripHtmlTags(content);
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highlight"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 text-lg">{error}</p>
        <Link 
          to="/news" 
          className="mt-4 inline-block px-4 py-2 bg-highlight text-white rounded hover:bg-highlight/80 transition-colors"
        >
          Back to News
        </Link>
      </div>
    );
  }

  // Single post view
  if (selectedPost) {
    return (
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <Link to="/news" className="mb-6 inline-block" style={{ color: 'var(--highlight)' }}>
          ← Back to News
        </Link>

        <article className="rounded-lg shadow-lg overflow-hidden bg-gray-50 dark:bg-gray-800/50">
          {selectedPost.headerImage && (
            <div className="aspect-video w-full">
              <img 
                src={selectedPost.headerImage} 
                alt={selectedPost.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          <div className="p-6 lg:p-8">
            <div className="mb-4">
              <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {selectedPost.title}
              </h1>
              <div className="flex items-center text-sm" style={{ color: 'var(--text-secondary)' }}>
                <span>By {formatAuthor(selectedPost.author)}</span>
                <span className="mx-2">•</span>
                <span>{formatDate(selectedPost.publishDate)}</span>
              </div>
            </div>
            
            <div 
              className="prose prose-lg max-w-none"
              style={{ color: 'var(--text-primary)' }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedPost.content, { USE_PROFILES: { html: true } }) }}
            />
          </div>
        </article>
      </div>
    );
  }

  // News listing view
  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <Link to="/" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
        ← Back to Home
      </Link>

      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
          News
        </h1>
        <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
          Latest updates and announcements
        </p>
      </div>

      {newsData && newsData.posts.length > 0 ? (
        <div className="space-y-8">
          {newsData.posts.map((post) => (
            <article 
              key={post.id} 
              className="bg-gray-50 dark:bg-gray-800/50 rounded-lg shadow overflow-hidden hover:shadow-xl transition-shadow"
            >
              <div className="md:flex">
                {post.headerImage && (
                  <div className="md:w-1/3">
                    <div className="aspect-video md:aspect-square w-full">
                      <img 
                        src={post.headerImage} 
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
                
                                 <div className={`p-6 ${post.headerImage ? 'md:w-2/3' : 'w-full'}`}>
                   <div className="mb-4">
                     <h2 className="text-2xl font-bold mb-2 hover:text-highlight transition-colors">
                       <Link to={`/news/${post.slug}`} style={{ color: 'var(--text-primary)' }}>
                         {post.title}
                       </Link>
                     </h2>
                    <div className="flex items-center text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                      {/* <span>By {formatAuthor(post.author)}</span>
                      <span className="mx-2">•</span> */}
                      <span>{formatDate(post.publishDate)}</span>
                    </div>
                  </div>
                  
                  <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
                    {getExcerpt(post.content)}
                  </p>
                  
                                     <Link 
                     to={`/news/${post.slug}`}
                     className="inline-flex items-center font-medium transition-colors"
                     style={{ color: 'var(--highlight)' }}
                   >
                    Read more
                    <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </article>
          ))}

          {newsData.hasMore && (
            <div className="text-center">
              <button
                onClick={loadMorePosts}
                disabled={loadingMore}
                className="px-6 py-3 bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors disabled:opacity-50"
                style={{ color: 'var(--text-primary)' }}
              >
                {loadingMore ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-highlight mr-2"></div>
                    Loading...
                  </div>
                ) : (
                  'Load More Posts'
                )}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-xl" style={{ color: 'var(--text-secondary)' }}>
            No news posts available yet.
          </p>
        </div>
      )}
    </div>
  );
};

export default News; 